/**
 * referral-landing-page.tsx — Trang link giới thiệu công khai `/r/:mã` (KHÔNG cần đăng nhập, ngoài AppLayout).
 *
 * Nằm trên tên miền hệ thống; KHÔNG chuyển hướng ra ngoài (không có open redirect). Khi mở:
 *   1) tra mã (tên đối tác đã che, ưu đãi nếu có);
 *   2) ghi lượt nhấp + nhận vé giới thiệu ký bởi server, lưu trên trình duyệt `cookieDays` ngày theo first/last-touch;
 *   3) nút "Đăng ký miễn phí" → /register?ref=<mã> (tự tạo tài khoản, gắn người giới thiệu source=signup);
 *      hoặc form "nhận tư vấn" → lead cho quản trị viên tạo tổ chức.
 * Backend: affiliate-routes.ts (affiliatePublicRoutes).
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BadgePercent, CheckCircle2, Handshake, Loader2 } from 'lucide-react'
import { BrandLogo } from '@/components/shared/brand-logo'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { API_BASE } from '@/lib/config'
import { i18n } from '@/lib/i18n'
import { discountText, normalizeCode, type AttributionModel, type DiscountMode } from '@/lib/affiliate'
import { readStoredRef, saveRefTicket, visitorId } from '@/lib/affiliate-ref-storage'
import { captchaPendingMessage, withTurnstileToken } from '@/lib/turnstile'
import { TurnstileBox, useTurnstile } from '@/components/shared/turnstile-box'
import { useSignupConfig } from '@/pages/auth/auth-api'

interface CodeInfo {
  code: string
  partnerName: string
  cookieDays: number
  attributionModel: AttributionModel
  discount: { mode: DiscountMode; value: number; maxDiscountAmount: number | null; purchases: number; code: string } | null
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((data as { error?: string }).error || i18n.t('growth:referralLanding.genericError'))
  return data as T
}

export function ReferralLandingPage() {
  const { t } = useTranslation('growth')
  const { code: rawCode = '' } = useParams<{ code: string }>()
  const code = normalizeCode(rawCode)
  const [info, setInfo] = useState<CodeInfo | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'invalid'>('loading')
  const tracked = useRef(false)

  useEffect(() => {
    if (tracked.current) return
    tracked.current = true
    ;(async () => {
      try {
        const res = await fetch(`${API_BASE}/public/affiliate/code/${encodeURIComponent(code)}`)
        if (!res.ok) throw new Error('invalid')
        const data = (await res.json()) as CodeInfo
        setInfo(data)
        setState('ok')
        const click = await postJson<{ ticket: string | null; cookieDays: number; attributionModel: AttributionModel }>(
          '/public/affiliate/click', { code, visitorId: visitorId() },
        ).catch(() => null)
        if (click?.ticket) saveRefTicket(data.code, click.ticket, click.cookieDays, click.attributionModel)
      } catch {
        setState('invalid')
      }
    })()
  }, [code])

  return (
    <div className="min-h-screen bg-shell px-4 py-8">
      <div className="mx-auto max-w-xl space-y-5">
        <div className="flex items-center justify-between">
          <BrandLogo variant="header" />
          <Link to="/login" className="text-sm text-primary hover:underline">{t('referralLanding.login')}</Link>
        </div>
        {state === 'loading' && <Loading label={t('referralLanding.loading')} />}
        {state === 'invalid' && (
          <Card className="p-5">
            <EmptyState title={t('referralLanding.invalidTitle')} description={t('referralLanding.invalidDesc')} />
          </Card>
        )}
        {state === 'ok' && info && <Landing info={info} />}
      </div>
    </div>
  )
}

function Landing({ info }: { info: CodeInfo }) {
  const { t } = useTranslation('growth')
  const [f, setF] = useState({ fullName: '', phone: '', email: '', companyName: '', note: '', website: '' })
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { config } = useSignupConfig()
  const ts = useTurnstile(config?.turnstileSiteKey, 'affiliate-lead')
  const disc = info.discount ? discountText(info.discount) : null
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (f.fullName.trim().length < 2) return setError(t('referralLanding.nameRequired'))
    if (!/^\+?\d{9,15}$/.test(f.phone.replace(/[\s.-]/g, ''))) return setError(t('referralLanding.phoneInvalid'))
    if (ts.pending) return setError(captchaPendingMessage())
    setSending(true)
    try {
      const stored = readStoredRef()
      await postJson('/public/affiliate/leads', withTurnstileToken({ ...f, phone: f.phone.replace(/[\s.-]/g, ''), code: info.code, ticket: stored?.ticket }, ts.token))
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referralLanding.sendFailed'))
      ts.reset() // token Turnstile dùng được một lần
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Card className="space-y-3 p-5">
        <div className="flex items-center gap-2 text-primary">
          <Handshake className="h-5 w-5" />
          <p className="text-sm font-medium">{t('referralLanding.referredBy', { name: info.partnerName })}</p>
        </div>
        <h1 className="text-xl font-semibold">{t('referralLanding.heroTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('referralLanding.heroDesc')}
        </p>
        <Button asChild className="w-full sm:w-auto">
          <Link to={`/register?ref=${encodeURIComponent(info.code)}`}>{t('referralLanding.registerFree')}</Link>
        </Button>
        {disc && info.discount && (
          <p className="rounded-md bg-primary/5 p-3 text-sm">
            <BadgePercent className="mr-1 inline h-4 w-4 text-primary" />
            {t('referralLanding.discountPre', { disc })} <b>{info.discount.code}</b> {t('referralLanding.discountPost')}
          </p>
        )}
      </Card>
      <Card className="p-5">
        {done ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <p className="font-medium">{t('referralLanding.receivedTitle')}</p>
            <p className="text-sm text-muted-foreground">{t('referralLanding.receivedDesc')}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <p className="font-medium">{t('referralLanding.leadFormTitle')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label htmlFor="lf-name">{t('referralLanding.fullName')}</Label><Input id="lf-name" value={f.fullName} onChange={(e) => set('fullName', e.target.value)} autoComplete="name" /></div>
              <div className="space-y-1.5"><Label htmlFor="lf-phone">{t('referralLanding.phone')}</Label><Input id="lf-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} inputMode="tel" autoComplete="tel" /></div>
              <div className="space-y-1.5"><Label htmlFor="lf-email">{t('referralLanding.email')}</Label><Input id="lf-email" type="email" value={f.email} onChange={(e) => set('email', e.target.value)} autoComplete="email" /></div>
              <div className="space-y-1.5"><Label htmlFor="lf-company">{t('referralLanding.company')}</Label><Input id="lf-company" value={f.companyName} onChange={(e) => set('companyName', e.target.value)} autoComplete="organization" /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="lf-note">{t('referralLanding.note')}</Label><Textarea id="lf-note" rows={3} value={f.note} onChange={(e) => set('note', e.target.value)} maxLength={1000} /></div>
            {/* honeypot chống bot — người thật không thấy ô này */}
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={(e) => set('website', e.target.value)} />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <TurnstileBox ts={ts} />
            <Button type="submit" className="w-full" disabled={sending || ts.pending}>
              {sending && <Loader2 className="h-4 w-4 animate-spin" />} {t('referralLanding.submit')}
            </Button>
            <p className="text-xs text-muted-foreground">{t('referralLanding.privacyNote')}</p>
          </form>
        )}
      </Card>
    </>
  )
}
